import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock } from "lucide-react";
import { CONFIG } from "@/config";
import { useWatchTime } from "@/hooks/useWatchTime";

const SDK_URL = "https://player.vimeo.com/api/player.js";

// Loads the official Vimeo Player SDK only after registration
function loadVimeoSdk() {
  if (window.Vimeo && window.Vimeo.Player) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let script = document.querySelector(`script[src="${SDK_URL}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = SDK_URL;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", reject, { once: true });
  });
}

export const VideoStage = ({ viewer, onProgress }) => {
  const containerRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [aspect, setAspect] = useState("16 / 9");
  // Watch time is only used to report minutesWatched with enquiries
  const { watched } = useWatchTime(player, 0);

  useEffect(() => {
    let cancelled = false;
    let instance = null;
    loadVimeoSdk()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        instance = new window.Vimeo.Player(containerRef.current, {
          url: CONFIG.VIMEO_URL,
          responsive: true,
          byline: false,
          portrait: false,
          title: false,
          autoplay: true,
          speed: true,
        });
        instance.ready().then(() => {
          instance.play().catch(() => {});
          Promise.all([instance.getVideoWidth(), instance.getVideoHeight()])
            .then(([w, h]) => w && h && setAspect(`${w} / ${h}`))
            .catch(() => {});
        }).catch(() => setLoadError(true));
        setPlayer(instance);
      })
      .catch(() => setLoadError(true));
    return () => {
      cancelled = true;
      if (instance) instance.destroy().catch(() => {});
    };
  }, []);

  useEffect(() => {
    onProgress(watched);
  }, [watched, onProgress]);

  const firstName = viewer.name.split(/\s+/)[0];

  return (
    <section className="pt-6 sm:pt-10" data-testid="video-stage">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="eyebrow">
          <span className="h-1.5 w-1.5 rounded-full bg-[#B6F23C] shadow-[0_0_12px_#B6F23C]" />
          Now playing
        </span>
        <h1 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-[#F4F5F2]" data-testid="greeting-banner">
          {CONFIG.video.greeting.replace("{name}", firstName)}
        </h1>
        <p className="mt-3 max-w-2xl text-sm sm:text-base text-[#A3A89E]" data-testid="video-note">
          {CONFIG.video.note}
        </p>
        <p className="mt-3 inline-flex items-center gap-2 text-xs sm:text-sm text-[#D9B25F]" data-testid="video-expiry-notice">
          <CalendarClock className="h-3.5 w-3.5 shrink-0" />
          {CONFIG.gate.expiryText}
        </p>
      </motion.div>

      <motion.div
        className="mt-8 player-frame"
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
      >
        <div ref={containerRef} className="player-inner" style={{ aspectRatio: aspect }} data-testid="vimeo-player-container" />
        {loadError && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-[#A3A89E] px-6 text-center" data-testid="video-error">
            The video could not be loaded. Please refresh the page to try again.
          </p>
        )}
      </motion.div>
    </section>
  );
};
